// The day timeline: every theatre in service as a column, bookings and blocks
// as blocks of time.
//
// Uses react-big-calendar (the project's established calendar, from the
// appointments module) in `day` view with operating rooms as RESOURCES — which
// is what turns one shared timeline into a column per theatre.
//
// A booking is an arbitrary [start, end) interval, not a slot (ADR-0002), so the
// calendar is rendered from real datetimes and never from a slot grid.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { Box, Button, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import "react-big-calendar/lib/css/react-big-calendar.css";

import { IPD_COLORS, IpdDateField, IpdSectionCard } from "../../ipd/utils";
import { fetchSchedule } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import { errorMessage } from "../components/common";
import { OT_BASE } from "../constants";

const localizer = momentLocalizer(moment);

function todayKey() {
  return moment().format("YYYY-MM-DD");
}

export default function OtSchedule() {
  const history = useHistory();
  const [date, setDate] = useState(todayKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (day) => {
    setLoading(true);
    setError("");
    try {
      setData(await fetchSchedule({ date: day }));
    } catch (err) {
      setError(errorMessage(err, "Could not load the schedule"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(date);
  }, [load, date]);

  // Memoised because the two useMemos below depend on it; a fresh [] each
  // render would rebuild the whole event list on every keystroke.
  const rooms = useMemo(() => data?.rooms || [], [data]);

  const resources = useMemo(
    () => rooms.map((r) => ({ id: r.id, title: r.name })),
    [rooms]
  );

  /**
   * Bookings and blocks become one event list, distinguished by `kind`.
   * A block occupies theatre time but has no case — showing them in one
   * timeline is the whole point, because "booked" and "unavailable" both mean
   * the room is not free.
   */
  const events = useMemo(() => {
    const out = [];
    rooms.forEach((room) => {
      (room.bookings || []).forEach((b) => {
        out.push({
          kind: "booking",
          id: `b-${b.id}`,
          resourceId: room.id,
          start: new Date(b.start_datetime),
          end: new Date(b.end_datetime),
          title: `${b.ot_case?.patient_name || "Case"} — ${
            b.ot_case?.primary_surgeon_name || "Surgeon Unassigned"
          }`,
          caseId: b.ot_case?.id,
          tracking: b.ot_case?.tracking_status,
          schedulingType: b.ot_case?.scheduling_type,
        });
      });
      (room.blocks || []).forEach((blk) => {
        out.push({
          kind: "block",
          id: `k-${blk.id}`,
          resourceId: room.id,
          start: new Date(blk.start_datetime),
          end: new Date(blk.end_datetime),
          title: `${blk.blocked_for === "SURGEON" ? blk.surgeon_name || "Surgeon" : "Maintenance"}${
            blk.reason ? ` — ${blk.reason}` : ""
          }`,
        });
      });
    });
    return out;
  }, [rooms]);

  const eventStyle = useCallback((event) => {
    if (event.kind === "block") {
      return {
        style: {
          backgroundColor: IPD_COLORS.border,
          color: IPD_COLORS.textSecondary,
          border: `1px dashed ${IPD_COLORS.textMuted}`,
          fontSize: 11.5,
        },
      };
    }
    // Navy by default. Two things earn a colour on a timetable: a case that
    // was never planned, and a theatre that is occupied right now. Colouring
    // every block by its tracking rung turned the day into a fruit salad, and
    // half the rungs are too light to carry white text anyway.
    const emergency = event.schedulingType === "EMERGENCY";
    const operating = event.tracking === "IN_PROGRESS";
    const color = emergency || operating ? "#C62828" : IPD_COLORS.primary;
    return {
      style: {
        backgroundColor: color,
        borderColor: color,
        color: "#fff",
        fontSize: 11.5,
        fontWeight: 600,
      },
    };
  }, []);

  const shift = (days) =>
    setDate(moment(date, "YYYY-MM-DD").add(days, "days").format("YYYY-MM-DD"));

  return (
    <OtScreen
      title="Operating Theatre Master Schedule"
      description="Multi-theatre diary and room utilization grid with live case allocations and scheduled maintenance blocks."
      loading={loading}
      error={error}
      onRetry={() => load(date)}
      actions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button size="small" onClick={() => shift(-1)} startIcon={<ChevronLeftIcon />}>
            Previous Day
          </Button>
          <Button size="small" variant="outlined" onClick={() => setDate(todayKey())}>
            Today
          </Button>
          <Button size="small" onClick={() => shift(1)} endIcon={<ChevronRightIcon />}>
            Next Day
          </Button>
          <Box sx={{ width: 170 }}>
            {/* IpdDateField hands back the ISO string itself, not an event. */}
            <IpdDateField value={date} onChange={(iso) => iso && setDate(iso)} />
          </Box>
        </Box>
      }
    >
      <IpdSectionCard
        title={moment(date, "YYYY-MM-DD").format("dddd, D MMMM YYYY")}
        subtitle={`${rooms.length} Operating Theatre(s) in Active Service — Select a booking block to inspect case details`}
      >
        {rooms.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ color: IPD_COLORS.textSecondary }}>
              No active operating theatres configured. Configure theatre rooms in OT Masters.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: "calc(100dvh - 330px)", minHeight: 420, p: 1.5 }}>
            <Calendar
              localizer={localizer}
              events={events}
              defaultView="day"
              views={["day"]}
              date={moment(date, "YYYY-MM-DD").toDate()}
              onNavigate={(d) => setDate(moment(d).format("YYYY-MM-DD"))}
              resources={resources}
              resourceIdAccessor="id"
              resourceTitleAccessor="title"
              step={15}
              timeslots={4}
              toolbar={false}
              eventPropGetter={eventStyle}
              onSelectEvent={(event) => {
                if (event.kind === "booking" && event.caseId) {
                  history.push(`${OT_BASE}/case/${event.caseId}`);
                }
              }}
              style={{ height: "100%" }}
            />
          </Box>
        )}
      </IpdSectionCard>

      <Box sx={{ mt: 1.5, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Legend color="#C62828" label="Emergency case" />
        <Legend color={IPD_COLORS.primary} label="Scheduled case" />
        <Legend color={IPD_COLORS.border} label="Maintenance / Room Block" />
      </Box>
    </OtScreen>
  );
}

function Legend({ color, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: 0.5,
          backgroundColor: color,
          border: `1px solid ${color}`,
        }}
      />
      <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>{label}</Typography>
    </Box>
  );
}
